import { useState, type ReactNode } from "react";
import { Accordion, Popover, Tooltip } from "@heroui/react";
import { Link } from "react-router";
import { Icon, IconSlot } from "../Icon";
import { CheckToggle } from "./CheckToggle";
import { IngestionIcon } from "../IngestionIcon";
import type { Source, SubSource } from "../../data/mock";

/** The ticked rows by id; "mixed" is a parent with only some of its children ticked (aria-checked="mixed"). */
export type Selection = Record<string, boolean | "mixed">;

/**
 * Il contenuto del popover del pin di un ente: la scelta delle cartelle
 * (Preferiti e i feed dell'utente). `close` chiude il popover; `setBusy`
 * dice che una chiamata è in corso: finché lo è, Esc e un clic fuori non
 * chiudono il popover.
 */
export type FavoriteChooser = (source: Source, close: () => void, setBusy: (busy: boolean) => void) => ReactNode;

type SourceListProps = {
  sources: Source[];
  selection: Selection;
  onSelect: (item: Source | SubSource, checked: boolean) => void;
  /** Se dato, il pin di ogni ente apre un popover con questo contenuto. */
  favoriteChooser?: FavoriteChooser;
  /** Fonti dei feed preferiti: checkbox non modificabili. */
  readOnly?: boolean;
  /** Mostra gli indicatori di avviso / contenuto strutturato / preferito. */
  showIndicators?: boolean;
  /** Se dato, il nome di un ente o di una sezione è un link a questa destinazione (la vista di quella fonte). */
  linkTo?: (item: Source | SubSource) => string | null;
  /** Enti aperti all'avvio (Parlamento nel pannello delle fonti). */
  defaultExpandedKeys?: string[];
};

const chevronClass = (open: boolean) => `transition-transform ${open ? "rotate-180" : ""}`;

const NAME_CLASS = "flex-1 text-sm leading-6 font-medium text-accent";

/** Lista fonti a fisarmonica del pannello laterale. */
export function SourceList({
  sources,
  selection,
  onSelect,
  favoriteChooser,
  readOnly,
  showIndicators = true,
  linkTo,
  defaultExpandedKeys,
}: SourceListProps) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set(defaultExpandedKeys ?? []));

  return (
    <Accordion
      allowsMultipleExpanded
      expandedKeys={expandedKeys}
      onExpandedChange={(keys) => setExpandedKeys(new Set([...keys].map(String)))}
      className="w-full"
    >
      {sources.map((source) => {
        const open = expandedKeys.has(source.id);
        const expandable = Boolean(source.children?.length);
        const href = linkTo?.(source) ?? null;
        const indicators = showIndicators && (
          <>
            {source.warning ? (
              source.healthDetail ? (
                // slot del triangolo: l'icona con il tooltip è sovrapposta sotto, come quella di ingestion
                <IconSlot />
              ) : (
                <Icon name="triangleExclamation" label="Fonte con avvisi" />
              )
            ) : (
              <IconSlot />
            )}
            {/* slot dell'icona ingestion: sovrapposta sotto, perché ha un tooltip focusabile */}
            <IconSlot />
            {/* slot del pin: il pulsante è sovrapposto (non può stare dentro il trigger) */}
            <IconSlot />
          </>
        );
        const chevron = expandable ? (
          <Icon name={open ? "chevronDownAccent" : "chevronDown"} className={chevronClass(open)} />
        ) : (
          <IconSlot />
        );
        return (
          <Accordion.Item
            key={source.id}
            id={source.id}
            className={`after:bg-border ${open ? "after:inset-x-4 after:w-auto after:bg-separator-secondary" : ""} ${
              source.warning ? "bg-[linear-gradient(var(--warning-soft),var(--warning-soft)),linear-gradient(var(--white),var(--white))]" : ""
            }`}
          >
            <Accordion.Heading className="flex items-center gap-3 p-4">
              <CheckToggle
                checked={selection[source.id] === true}
                indeterminate={selection[source.id] === "mixed"}
                label={`Seleziona ${source.name}`}
                readOnly={readOnly}
                onChange={(checked) => onSelect(source, checked)}
              />
              {href ? (
                // il nome porta alla vista della fonte; solo il chevron apre l'ente
                <>
                  <Link to={href} className={`${NAME_CLASS} rounded-sm`}>
                    {source.name}
                  </Link>
                  {indicators}
                  <Accordion.Trigger
                    isDisabled={!expandable}
                    aria-label={open ? `Chiudi ${source.name}` : `Apri ${source.name}`}
                    className="flex items-center rounded-sm p-0 hover:bg-transparent disabled:opacity-100"
                  >
                    {chevron}
                  </Accordion.Trigger>
                </>
              ) : (
                <Accordion.Trigger
                  isDisabled={!expandable}
                  className="flex flex-1 items-center gap-3 rounded-sm p-0 hover:bg-transparent disabled:opacity-100"
                >
                  <span className={NAME_CLASS}>{source.name}</span>
                  {indicators}
                  {chevron}
                </Accordion.Trigger>
              )}
            </Accordion.Heading>
            {showIndicators && source.warning && source.healthDetail && (
              <HealthTooltip detail={source.healthDetail} className="absolute top-5 right-[100px]">
                <Icon name="triangleExclamation" label="Fonte con avvisi" />
              </HealthTooltip>
            )}
            {showIndicators && favoriteChooser && <FavoriteToggle source={source} chooser={favoriteChooser} />}
            {readOnly && source.favorite && <FavoriteBadge />}
            {showIndicators && source.hasCode && <IngestionIcon className="absolute top-5 right-[72px]" />}
            {expandable && (
              <Accordion.Panel>
                <div className="flex flex-col pr-6 pb-2 pl-4">
                  {source.children!.map((child) => (
                    <SubSourceRow
                      key={child.id}
                      item={child}
                      selection={selection}
                      onSelect={onSelect}
                      linkTo={linkTo}
                      depth={0}
                    />
                  ))}
                </div>
              </Accordion.Panel>
            )}
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

/** Tooltip con il dettaglio dello stato (`health_detail`), nello stile di quello dell'icona ingestion. */
function HealthTooltip({ detail, className, children }: { detail: string; className?: string; children: ReactNode }) {
  return (
    <Tooltip delay={300}>
      <Tooltip.Trigger aria-label={detail} className={`cursor-help rounded-xs ${className ?? ""}`}>
        {children}
      </Tooltip.Trigger>
      <Tooltip.Content
        showArrow
        placement="bottom"
        className="max-w-[260px] rounded-xl px-2.5 py-1.5 text-xs leading-5 break-normal whitespace-pre-line text-muted"
      >
        <Tooltip.Arrow />
        <p>{detail}</p>
      </Tooltip.Content>
    </Tooltip>
  );
}

/** L'etichetta del pin: acceso (anche in parte) apre la gestione delle cartelle, spento salva nei preferiti. */
function favoriteLabel(source: Pick<Source, "name" | "favorite" | "favoritePartly">): string {
  if (!source.favorite) return `Salva ${source.name} nei preferiti`;
  return source.favoritePartly
    ? `Preferito (alcune sezioni): gestisci le cartelle di ${source.name}`
    : `Preferito: gestisci le cartelle di ${source.name}`;
}

/**
 * Pin "preferito" nello slot prima del chevron: apre il popover con la
 * scelta delle cartelle (lo stesso overlay del menu utente in AppHeader).
 */
function FavoriteToggle({ source, chooser }: { source: Source; chooser: FavoriteChooser }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setBusy(false);
    setOpen(false);
  };
  return (
    <Popover
      isOpen={open}
      onOpenChange={(next) => {
        // una chiamata in corso: il popover resta aperto, la chiude il contenuto quando ha finito
        if (!next && busy) return;
        if (next) setBusy(false);
        setOpen(next);
      }}
    >
      <Popover.Trigger
        aria-label={favoriteLabel(source)}
        className="absolute top-5 right-[44px] cursor-pointer rounded-xs"
      >
        <Icon name={source.favorite ? "pin" : "pinOutline"} />
      </Popover.Trigger>
      <Popover.Content placement="bottom end" className="w-72 rounded-xl">
        <Popover.Dialog aria-label={source.favorite ? `Cartelle di ${source.name}` : `Salva ${source.name} nei preferiti`}>
          {chooser(source, close, setBusy)}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

function FavoriteBadge() {
  return (
    <span className="pointer-events-none absolute top-5 right-[44px]">
      <Icon name="pin" label="Preferito" />
    </span>
  );
}

type SubSourceRowProps = {
  item: SubSource;
  selection: Selection;
  onSelect: (item: SubSource, checked: boolean) => void;
  linkTo?: (item: SubSource) => string | null;
  depth: number;
};

function SubSourceRow({ item, selection, onSelect, linkTo, depth }: SubSourceRowProps) {
  const [open, setOpen] = useState(Boolean(item.defaultOpen));
  const checked = selection[item.id] === true;
  const href = linkTo?.(item) ?? null;
  const labelClass = `text-sm leading-6 ${item.failing ? "text-danger line-through" : "text-accent"}`;
  const text = href ? (
    <Link to={href} className={`${labelClass} rounded-sm`}>
      {item.name}
    </Link>
  ) : (
    <span className={labelClass}>{item.name}</span>
  );
  const label =
    item.failing && item.healthDetail ? (
      <HealthTooltip detail={item.healthDetail} className="flex items-center">
        {text}
      </HealthTooltip>
    ) : (
      text
    );
  const indent = depth === 0 ? "pl-7" : "pl-[54px]";

  if (!item.children?.length) {
    return (
      <div className={`flex items-center gap-3 ${depth === 0 ? "py-2" : "py-1.5"} ${indent}`}>
        <CheckToggle checked={checked} failing={item.failing} label={`Seleziona ${item.name}`} onChange={(c) => onSelect(item, c)} />
        {label}
      </div>
    );
  }

  // Toggle semplice: un Disclosure annidato in un pannello chiuso misurerebbe altezza 0.
  return (
    <div>
      <div className={`flex items-center gap-3 py-2 ${indent}`}>
        <CheckToggle checked={checked} label={`Seleziona ${item.name}`} onChange={(c) => onSelect(item, c)} />
        {href ? (
          <>
            <span className="flex flex-1">{label}</span>
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? `Chiudi ${item.name}` : `Apri ${item.name}`}
              onClick={() => setOpen((o) => !o)}
              className="flex cursor-pointer items-center rounded-sm"
            >
              <Icon name="chevronDown" className={`-mr-2 ${chevronClass(open)}`} />
            </button>
          </>
        ) : (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="flex flex-1 cursor-pointer items-center justify-between rounded-sm text-left"
          >
            {label}
            <Icon name="chevronDown" className={`-mr-2 ${chevronClass(open)}`} />
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-col pb-1">
          {item.children.map((child) => (
            <SubSourceRow
              key={child.id}
              item={child}
              selection={selection}
              onSelect={onSelect}
              linkTo={linkTo}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
